import React from 'react';
import { Box, Typography, Button, Paper, Chip, Popover, Stack, IconButton } from '@mui/material';
import { EmojiEvents, Share, Facebook, X } from '@mui/icons-material';
import AdComponent from './ads';
import { handleNativeShare, handleFacebookShare, generateShareUrls } from '../utils/shareUtils';

interface VictoryPopupProps {
  open: boolean;
  onClose: () => void;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  score: number;
  isMobile: boolean;
}

const VictoryPopup: React.FC<VictoryPopupProps> = ({
  open,
  onClose,
  firstName,
  middleName,
  lastName,
  score,
  isMobile
}) => {
  const fullName = `${firstName || ''} ${middleName || ''} ${lastName || ''}`.trim();

  const renderMobileShare = () => (
    <Box sx={{ mb: 3 }}>
      <Button
        onClick={() => handleNativeShare(score)}
        variant="contained"
        size="large"
        startIcon={<Share />}
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': { bgcolor: 'primary.dark' },
          px: 3,
          py: 1.5
        }}
      >
        Share My Score
      </Button>
    </Box>
  );

  const renderDesktopShare = () => (
    <Stack justifyContent="center" alignItems="center" direction="row" spacing={2} sx={{ mb: 3 }}>
      <IconButton
        onClick={() => handleFacebookShare(score)}
        sx={{
          bgcolor: '#1877f2',
          color: 'white',
          '&:hover': { bgcolor: '#166fe5' }
        }}
      >
        <Facebook fontSize="large" />
      </IconButton>
      <IconButton
        href={generateShareUrls(score).twitter}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          bgcolor: '#000000',
          color: 'white',
          '&:hover': { bgcolor: '#333333' }
        }}
      >
        <X fontSize="large" />
      </IconButton>
      <IconButton
        onClick={() => handleNativeShare(score)}
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': { bgcolor: 'primary.dark' }
        }}
      >
        <Share fontSize="large" />
      </IconButton>
    </Stack>
  );

  return (
    <Popover
      onClose={onClose}
      open={open}
      anchorReference="none"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper elevation={6} sx={{ maxWidth: { xs: '95vw', sm: 380, md: 440, lg: 500 }, mx: { xs: 1, sm: 0 } }}>
        <Box sx={{ px: { xs: 0.75, sm: 0.5, md: 1.5, lg: 4 }, py: { xs: 0.75, sm: 0.5, md: 1.5, lg: 4 }, textAlign: 'center' }}>
          <EmojiEvents sx={{ fontSize: { xs: 40, sm: 48, md: 54, lg: 60 }, color: 'gold', mb: { xs: 1, sm: 1.5, md: 2 } }} />

          <Typography
            variant="h4"
            color="primary"
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.125rem' } }}
          >
            🔥 Look at you go! 🔥
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: { xs: 1, sm: 1.5, md: 2 },
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem', lg: '1.5rem' }
            }}
          >
            {fullName}!
          </Typography>

          <Chip
            label={`Final Score: ${score}`}
            color="success"
            size="medium"
            sx={{
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem', lg: '1.1rem' },
              fontWeight: 'bold',
              mb: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, sm: 0.85, md: 1 }
            }}
          />

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem', lg: '1.25rem' }
            }}
          >
            Time to flex on your friends! 💪
          </Typography>

          {isMobile ? renderMobileShare() : renderDesktopShare()}

          <AdComponent />

          <Button
            id="congrats"
            variant="contained"
            size="large"
            onClick={onClose}
            sx={{
              mt: { xs: 1.5, sm: 2 },
              px: { xs: 1.5, sm: 1, md: 2 },
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
            }}
          >
            Continue Playing
          </Button>
        </Box>
      </Paper>
    </Popover>
  );
};

export default VictoryPopup;